import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AnimatePresence, MotionConfig } from 'motion/react';
import { last } from 'rambda';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useMatches, useSearchParams } from 'react-router';
import { useSessionStorage } from 'usehooks-ts';
import AppHelmet from '@/components/layouts/page-layout/app-helmet';
import AppPrompter from '@/components/layouts/page-layout/app-prompter';
import AppToast from '@/components/layouts/page-layout/app-toast';
import PwaPrompt from '@/components/pwa-prompt/pwa-prompt';
import { ScreenBreakpointIndicator } from '@/components/screen-breakpoint-indicator';
import { ThemeSwitcher } from '@/components/theme-switcher';
import useComm from '@/hooks/comm/use-comm';
import usePeriodicMessages from '@/hooks/conversations/use-periodic-messages';
import useNotificationPermission from '@/hooks/fcm/use-notification-permission';
import useClientId from '@/hooks/pwa/use-client-id';
import { useSubscribedSessionUnReadCountByUserId } from '@/hooks/sessions/use-conversation-sessions';
import useAppGuard from '@/hooks/use-app-guard';
import useUserPlatform, { useIsDesktop } from '@/hooks/use-user-platform';
import useWebSocketManager from '@/hooks/use-web-socket-manager';
import { handleSchema } from '@/routes';
import { RootLayoutContext } from '@/routes/root.context';
import { IS_DEV } from '@/utils/constants';

export default function RootRoute() {
  const [debugMode, setDebugMode] = useSessionStorage('debug-mode', false);
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const matches = useMatches();
  const { isDesktop, isEmbedded } = useUserPlatform();
  const { commManager } = useComm();

  useClientId();
  useAppGuard(!pathname.startsWith('/auth'));
  useNotificationPermission();
  usePeriodicMessages();
  useWebSocketManager();
  // useStorageAccessPermission();
  useSubscribedSessionUnReadCountByUserId();

  const showFooter = useMemo(() => {
    if (isDesktop) return true;
    const lastHandle = handleSchema.safeParse(last(matches)?.handle);
    return lastHandle.success && Boolean(lastHandle.data.showFooter);
  }, [matches, isDesktop]);

  useEffect(() => {
    if (commManager && isEmbedded) {
      commManager?.dispatch({ event: 'fullscreen', data: { toggle: !showFooter } });
    }
  }, [isEmbedded, showFooter, commManager]);

  useEffect(() => {
    if (!IS_DEV || !searchParams.has('debug')) return;

    if (searchParams.get('debug') === 'off') {
      setDebugMode(false);
    } else {
      setDebugMode(true);
    }
  }, [searchParams, setDebugMode]);

  return (
    <RootLayoutContext.Provider value={{ showFooter }}>
      <>
        <AppHelmet />

        <div
          id='wrapper'
          className='bg-neutral-0 flex flex-col items-stretch min-h-dvh w-screen desktop:mx-auto desktop:relative 3xl:shadow-2xl 3xl:rounded-2xl desktop:max-w-screen-3xl desktop:overflow-x-hidden'>
          <PwaPrompt />

          <AnimatedOutlet />
        </div>

        <AppToast />

        <AppPrompter />

        {IS_DEV && debugMode && (
          <>
            <ReactQueryDevtools buttonPosition='bottom-left' />
            <ScreenBreakpointIndicator />
            <ThemeSwitcher />
          </>
        )}
      </>
    </RootLayoutContext.Provider>
  );
}

export const AnimatedOutlet = () => {
  const isDesktop = useIsDesktop();

  if (isDesktop) return <Outlet />;

  return (
    <MotionConfig
      reducedMotion='user'
      transition={{ ease: 'circInOut', duration: 0.15 }}>
      <AnimatePresence
        mode='wait'
        initial={false}>
        <Outlet />
      </AnimatePresence>
    </MotionConfig>
  );
};
